import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const THEME_STORAGE_KEY = 'ra-explorer-theme';
const THEMES = { DARK: 'dark', LIGHT: 'light' };

const ThemeContext = createContext(null);

function getInitialTheme() {
  if (typeof window === 'undefined') return THEMES.DARK;
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === THEMES.LIGHT || stored === THEMES.DARK) return stored;
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? THEMES.LIGHT : THEMES.DARK;
}

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === THEMES.LIGHT) root.setAttribute('data-theme', 'light');
  else root.removeAttribute('data-theme');
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    localStorage.setItem(THEME_STORAGE_KEY, next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK);
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark: theme === THEMES.DARK }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
